import { formatDateTime } from '../../shared/dateTimeFormatting';
import { useDateTimePreferences } from '../../shared/DateTimeProvider';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { tenantApi, MobileDeviceRecord } from '../../services/tenantApi';

export const MobileDevicesManagement: React.FC = () => {
  useDateTimePreferences();
  const [devices, setDevices] = useState<MobileDeviceRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [busyInstallation, setBusyInstallation] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [userById, setUserById] = useState<Record<string, { displayName?: string; email?: string }>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [loadedDevices, loadedUsers] = await Promise.all([
        tenantApi.listMobileDevices(),
        tenantApi.getUsers({ take: 500 }),
      ]);

      const nextUserById: Record<string, { displayName?: string; email?: string }> = {};
      (loadedUsers.users || []).forEach((u) => {
        if (u.userId) {
          nextUserById[u.userId] = {
            displayName: u.displayName,
            email: u.email,
          };
        }
      });

      setUserById(nextUserById);
      setDevices(loadedDevices);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Failed to load devices');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const runAction = async (installationId: string, action: 'reregister' | 'revoke' | 'remove') => {
    setBusyInstallation(installationId);
    setStatus('');
    try {
      if (action === 'reregister') {
        await tenantApi.requestMobileDeviceReregister(installationId);
        setStatus(`Re-register requested for ${installationId}`);
      }
      if (action === 'revoke') {
        await tenantApi.revokeMobileDevice(installationId);
        setStatus(`Revoked ${installationId}`);
      }
      if (action === 'remove') {
        const confirmed = window.confirm(`Remove device ${installationId}? This cannot be undone.`);
        if (!confirmed) {
          return;
        }
        await tenantApi.removeMobileDevice(installationId);
        setStatus(`Removed ${installationId}`);
      }
      await load();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : `Failed to ${action} device`);
    } finally {
      setBusyInstallation(null);
    }
  };

  const filteredDevices = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return devices;
    }

    return devices.filter((d) => {
      const user = d.userId ? userById[d.userId] : undefined;
      const haystack = [
        d.installationId,
        d.platform,
        d.appVersion || '',
        d.userId || '',
        user?.displayName || '',
        user?.email || '',
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(term);
    });
  }, [devices, search, userById]);

  return (
    <div className="management-section mobile-devices-page">
      <div className="section-header" style={{ marginBottom: '0.75rem' }}>
        <h2>Mobile Devices</h2>
        <button className="btn-secondary" onClick={() => void load()} disabled={loading}>
          Refresh
        </button>
      </div>
      <p className="section-description" style={{ marginTop: 0 }}>
        Review enrolled devices for this tenant. Revoke blocks access for lost/stolen devices, re-register restores trusted access, and remove deletes obsolete records.
      </p>

      <div className="filters" style={{ marginBottom: '0.5rem' }}>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by user name, email, user ID, installation, platform, version"
          aria-label="Search mobile devices"
        />
      </div>

      {status ? <p className="table-info">{status}</p> : null}

      {loading ? (
        <p>Loading devices...</p>
      ) : filteredDevices.length === 0 ? (
        <p>No devices registered yet.</p>
      ) : (
        <div className="mobile-devices-table-scroll">
          <table className="users-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Installation</th>
                <th>Platform</th>
                <th>Version</th>
                <th>Online</th>
                <th>Revoked</th>
                <th>Re-register</th>
                <th>Last seen</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDevices.map((d) => {
                const user = d.userId ? userById[d.userId] : undefined;
                return (
                  <tr key={d.installationId}>
                    <td>
                      {user?.displayName || user?.email ? (
                        <>
                          <div>{user.displayName || 'Unknown user'}</div>
                          <div style={{ color: '#64748b', fontSize: '0.8rem' }}>{user.email || d.userId || '-'}</div>
                        </>
                      ) : (
                        <code>{d.userId || 'Unassigned'}</code>
                      )}
                    </td>
                    <td><code>{d.installationId}</code></td>
                    <td>{d.platform}</td>
                    <td>{d.appVersion || '-'}</td>
                    <td>{d.isOnline ? 'Yes' : 'No'}</td>
                    <td>{d.isRevoked ? 'Yes' : 'No'}</td>
                    <td>{d.reregisterRequired ? 'Yes' : 'No'}</td>
                    <td>{formatDateTime(d.lastSeenAt)}</td>
                    <td className="user-actions-cell">
                      <button
                        className="btn-sm btn-secondary"
                        disabled={busyInstallation === d.installationId}
                        onClick={() => void runAction(d.installationId, 'reregister')}
                      >
                        Re-register
                      </button>
                      <button
                        className="btn-sm btn-danger"
                        disabled={busyInstallation === d.installationId || d.isRevoked}
                        onClick={() => void runAction(d.installationId, 'revoke')}
                      >
                        Block (Revoke)
                      </button>
                      <button
                        className="btn-sm btn-secondary"
                        disabled={busyInstallation === d.installationId}
                        onClick={() => void runAction(d.installationId, 'remove')}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MobileDevicesManagement;
